import { Controller } from "@hotwired/stimulus"
import { fieldGroup, isFieldFilled, isFieldShown } from "../../src/shared/helpers/form_field_state"

// Shows one form section at a time, picked from a side menu. Hidden sections
// stay in the form, so saving submits all of them. The menu flags each section:
// server errors, activation requirements still missing, or all filled in.
// A section without requirements is done when a shown element of its panel
// says so (data-section-nav-complete="true", e.g. the training wishes).
// A browser validation error in a hidden section switches to that section, so
// the browser can focus the field.
// With step buttons, the submit button is secondary until the last section,
// where it replaces Next as the primary action.
// In a modal, the button opening it can name the section to show
// (data-section-nav-key), e.g. the activation badge of the practitioners table.
export default class extends Controller {
  static targets = ["link", "status", "panel", "previous", "next", "submit"]
  static classes = ["hidden", "missing", "complete", "done", "error", "primaryButton", "secondaryButton"]
  static values = { requirements: Array, missingTitle: String }

  connect () {
    this.form = this.element.closest("form") || this.element
    // Remembers the open section across re-renders: on the modal, which stays
    // when the server replaces its content frame (e.g. after activating),
    // else on the frame, whose content is swapped.
    this.modal = this.element.closest(".modal")
    this.memory = this.modal || this.element.closest("turbo-frame")

    this.refresh = this.refresh.bind(this)
    this.revealInvalid = this.revealInvalid.bind(this)
    this.openAtTrigger = this.openAtTrigger.bind(this)
    this.events = ["input", "change", "click"]
    this.events.forEach((event) => this.form.addEventListener(event, this.refresh))
    this.form.addEventListener("invalid", this.revealInvalid, true)
    this.modal?.addEventListener("show.bs.modal", this.openAtTrigger)

    this.element.querySelectorAll(".field_with_errors").forEach((element) => {
      const details = element.closest("details")
      if (details) details.open = true
    })
    this.select(this.initialKey())
    this.refresh()
    // Nested controllers (e.g. new vs existing user fields) connect after this
    // one and may hide or show requirement fields.
    this.refreshTimeout = setTimeout(this.refresh)
  }

  disconnect () {
    clearTimeout(this.refreshTimeout)
    this.events.forEach((event) => this.form.removeEventListener(event, this.refresh))
    this.form.removeEventListener("invalid", this.revealInvalid, true)
    this.modal?.removeEventListener("show.bs.modal", this.openAtTrigger)
  }

  show ({ params: { key } }) {
    this.select(key)
    this.scrollToTop()
  }

  previous () {
    this.step(-1)
  }

  next () {
    this.step(1)
  }

  // With step buttons, Enter in a field moves on instead of saving halfway.
  // The last section keeps the browser's submit. Text areas, buttons and
  // widgets that handle Enter themselves (Tom Select) are left alone.
  advance (event) {
    if (event.defaultPrevented || event.isComposing) return
    if (!this.hasNextTarget || this.nextTarget.hidden) return

    const field = event.target
    if (field.tagName !== "INPUT" || ["button", "submit", "checkbox", "radio", "file"].includes(field.type)) return

    event.preventDefault()
    this.next()
  }

  // Bootstrap passes the button that opened the modal as relatedTarget. A
  // nested modal opening inside this one bubbles up here without a key.
  openAtTrigger (event) {
    const key = event.relatedTarget?.dataset.sectionNavKey
    if (!this.keys.includes(key)) return

    this.select(key)
    this.scrollToTop()
  }

  reveal ({ detail: { element } }) {
    this.showPanelOf(element)
  }

  revealInvalid (event) {
    if (this.revealingInvalid) return

    this.revealingInvalid = true
    setTimeout(() => { this.revealingInvalid = false })
    this.showPanelOf(event.target)
  }

  showPanelOf (element) {
    const panel = this.panelTargets.find((target) => target.contains(element))
    if (!panel) return

    const details = element.closest("details")
    if (details) details.open = true

    this.select(panel.dataset.sectionKey)
  }

  step (offset) {
    const key = this.keys[this.keys.indexOf(this.currentKey) + offset]
    if (!key) return

    this.select(key)
    this.scrollToTop()
  }

  select (key) {
    this.currentKey = key
    if (this.memory) this.memory.dataset.sectionNavKey = key

    this.panelTargets.forEach((panel) => {
      panel.classList.toggle(this.hiddenClass, panel.dataset.sectionKey !== key)
    })
    this.linkTargets.forEach((link) => {
      if (link.dataset.sectionKey === key) {
        link.setAttribute("aria-current", "step")
        this.centerLink(link)
      } else {
        link.removeAttribute("aria-current")
      }
    })

    const index = this.keys.indexOf(key)
    const last = index >= this.keys.length - 1
    if (this.hasPreviousTarget) this.previousTarget.hidden = index <= 0
    if (this.hasNextTarget) this.nextTarget.hidden = last
    if (this.hasSubmitTarget && this.hasPrimaryButtonClass && this.hasSecondaryButtonClass) {
      this.submitTarget.classList.remove(...this.primaryButtonClasses, ...this.secondaryButtonClasses)
      this.submitTarget.classList.add(...(last ? this.primaryButtonClasses : this.secondaryButtonClasses))
    }
  }

  refresh () {
    const requirements = this.requirementsValue
      .map((fieldId) => fieldGroup(this.form, fieldId))
      .filter((group) => group && isFieldShown(group))
    const missing = requirements.filter((group) => !isFieldFilled(group))

    this.statusTargets.forEach((status) => {
      const key = status.dataset.sectionKey

      if (key === "activation") {
        const done = requirements.length - missing.length
        this.renderStatus(status, missing.length ? "missing" : "done", `${done} / ${requirements.length}`)
        return
      }

      const panel = this.panelTargets.find((target) => target.dataset.sectionKey === key)
      if (!panel) return

      if (panel.querySelector(".field_with_errors")) {
        this.renderStatus(status, "error")
        return
      }

      const own = requirements.filter((group) => panel.contains(group))
      const ownMissing = own.filter((group) => missing.includes(group))
      if (ownMissing.length) {
        this.renderStatus(status, "missing", String(ownMissing.length))
        if (this.missingTitleValue) status.title = this.missingTitleValue.replace("%{count}", ownMissing.length)
      } else {
        const completions = Array.from(panel.querySelectorAll("[data-section-nav-complete]")).filter(isFieldShown)
        const complete = completions.every((element) => element.dataset.sectionNavComplete === "true")
        this.renderStatus(status, complete && (own.length || completions.length) ? "done" : "")
      }
    })
  }

  // A done status with a count (Activation) is a success pill, without one a check.
  renderStatus (status, state, text = "") {
    status.dataset.state = state
    status.removeAttribute("title")
    status.classList.remove(...this.missingClasses, ...this.completeClasses, ...this.doneClasses, ...this.errorClasses)
    if (state === "missing") status.classList.add(...this.missingClasses)
    if (state === "done") status.classList.add(...(text ? this.completeClasses : this.doneClasses))
    if (state === "error") status.classList.add(...this.errorClasses)
    status.replaceChildren()

    if (!text && state) {
      const icon = document.createElement("i")
      icon.className = state === "error" ? "fa fa-circle-exclamation" : "fa fa-check"
      icon.setAttribute("aria-hidden", "true")
      status.append(icon)
    }
    if (text) status.append(text)
  }

  // On narrow screens the menu is a scrolling row: keep the current entry in
  // the middle. Only the row scrolls, never the modal body.
  centerLink (link) {
    const nav = link.closest("nav")
    if (!nav || nav.scrollWidth <= nav.clientWidth) return

    const navBox = nav.getBoundingClientRect()
    const linkBox = link.getBoundingClientRect()
    const offset = (linkBox.left + linkBox.width / 2) - (navBox.left + navBox.width / 2)
    nav.scrollBy?.({ left: offset, behavior: "smooth" })
  }

  scrollToTop () {
    this.element.closest(".modal-body")?.scrollTo?.({ top: 0 })
  }

  get keys () {
    return this.linkTargets.map((link) => link.dataset.sectionKey)
  }

  initialKey () {
    const invalid = this.panelTargets.find((panel) => panel.querySelector(".field_with_errors"))
    if (invalid) return invalid.dataset.sectionKey

    const remembered = this.memory?.dataset.sectionNavKey
    if (this.keys.includes(remembered)) return remembered

    return this.keys[0]
  }
}
